/**
 * composeCoverPrompt — the ONE renderer of a creation's identity into a cover /
 * splash art prompt (the gallery card + the in-game menu splash). Used by the
 * builder's auto cover generation, the studio's cover-prompt prefill, and the
 * regen-covers backfill — one composer so covers stay consistent wherever they
 * are made.
 *
 * The whole point of this file: DERIVE the art style from the specific game, so
 * covers stop converging on one house look. It feeds the image model the game's
 * subject (description + design summary + entities), its genre and mood (style +
 * design.theme), and then explicitly hands STYLE SELECTION to the model with a
 * menu of mediums — telling it NOT to default to neon/glow unless the theme
 * truly calls for it. The old fixed "dark neon arcade aesthetic" clamp is gone.
 */
import type { GameDesign } from './types.js';

export interface CoverPromptInput {
  title: string;
  description: string;
  /** Coarse genre (the gallery style enum: action/puzzle/shooter/…). */
  style: string;
  /** The mechanics authority when the creation has one — its summary, theme,
   *  and entities are what make each game's art distinct. Optional: pre-design
   *  and native creations compose from title/description/style alone. */
  design?: Pick<GameDesign, 'summary' | 'theme' | 'entities' | 'coreVerbs'> | null;
}

/** The palette of mediums the model chooses from — deliberately broad so games
 *  land on genuinely different looks. Neon is present but explicitly gated. */
const MEDIUMS =
  'painterly digital illustration, retro pixel art, bold flat vector, ' +
  'gritty comic-book ink, soft watercolor, 3D claymation render, cel-shaded ' +
  'anime, chunky low-poly, hand-drawn storybook, or chrome-and-neon synthwave';

/** Compose the cover/splash art prompt. Deterministic: identical input ⇒
 *  identical string (so a regenerate reproduces the same brief). */
export function composeCoverPrompt(input: CoverPromptInput): string {
  const title = input.title.trim() || 'this game';
  const genre = input.style.trim();
  const subject =
    input.design?.summary?.trim() ||
    input.description.trim() ||
    (genre ? `a ${genre} game` : 'an arcade game');

  const lines: string[] = [`Video-game KEY ART, 16:9 landscape, for "${title}".`, `Subject: ${subject}.`];

  const entities = (input.design?.entities ?? [])
    .map((e) => e.name.trim())
    .filter(Boolean)
    .slice(0, 6);
  if (entities.length) lines.push(`Featured elements: ${entities.join(', ')}.`);

  if (genre) lines.push(`Genre: ${genre}.`);
  const theme = input.design?.theme?.trim();
  if (theme) lines.push(`Mood & setting: ${theme}.`);

  lines.push(
    `Choose the single illustration STYLE that best fits THIS game's mood and ` +
      `setting — ${MEDIUMS}. Do NOT default to neon or glow unless the theme ` +
      `genuinely calls for it; let the subject drive the look. Commit fully to ` +
      `one coherent style.`,
    `Cinematic composition with a strong focal point, rich detail, vivid but ` +
      `theme-appropriate color, readable at thumbnail size. Absolutely NO text, ` +
      `words, letters, numbers, logos, UI, or watermarks.`,
  );
  return lines.join(' ');
}
